import { types, applySnapshot, isAlive } from "mobx-state-tree";
import { BaseEntity, isNewlyCreated, DictionaryItem } from "modules/common/models/entity";
import { Transport } from "modules/common/models/transport";
import { Notificator } from "modules/common/models/notificator";
import { apiUrls } from "modules/common/services/communication/urls";
import { flow } from "modules/common/models/flow";
import { EMPTY_OBJECT_ID, DATE_TIME_FORMAT } from "modules/common/constants";
import Schema from "../components/details/validation";
import moment from "moment";
import { texts } from "modules/common/texts";
import { nameof } from "modules/common/services/typescript";
import { getFieldLabel } from "modules/common/services/form/fields";

export const ProductionStageDictionaryItem = DictionaryItem.named("ProductionStageDictionaryItem");

const ProductionStageBase = types.compose(
    Transport,
    Notificator,
    BaseEntity,
    types.model({
        name: types.string,
        comment: types.string,
    })
);

export const ProductionStage = ProductionStageBase.actions((self) => ({
    load: flow(function* (id: string) {
        try {
            const snapshot = isNewlyCreated(id)
                ? initialState()
                : yield self.transport.get<ProductionStageSnapshotType>(apiUrls.productionStages.details(id));

            applySnapshot(self, snapshot);
        } catch (er) {
            self.notify.error(er);
        }
    }),

    save: flow(function* (model: ProductionStageSnapshotType) {
        try {
            const snapshot = self.isNewlyCreated
                ? yield self.transport.put<ProductionStageSnapshotType>(apiUrls.productionStages.create(), model)
                : yield self.transport.post<ProductionStageSnapshotType>(
                      apiUrls.productionStages.update(self.id),
                      model
                  );

            isAlive(self) && applySnapshot(self, snapshot);
            self.notify.success(texts.messages.saved);

            return true;
        } catch (er) {
            self.notify.error(er);

            return false;
        }
    }),

    delete: flow(function* () {
        if (self.isNewlyCreated) {
            return true;
        }

        try {
            yield self.transport.delete<boolean>(apiUrls.productionStages.delete(self.id));
            self.notify.success(texts.messages.removed);

            isAlive(self) && applySnapshot(self, initialState());
            return true;
        } catch (er) {
            self.notify.error(er);
            return false;
        }
    }),
})).named("ProductionStage");

export type ProductionStageSnapshotType = typeof ProductionStageBase.SnapshotType;
export type ProductionStageType = typeof ProductionStage.Type;
export type ProductionStageDictionaryItemType = typeof ProductionStageDictionaryItem.Type;

export const initialState = (): ProductionStageSnapshotType => ({
    id: EMPTY_OBJECT_ID,
    created: moment().format(DATE_TIME_FORMAT),
    name: "",
    comment: "",
});

export const fields = {
    name: nameof((a: ProductionStageType) => a.name) as string,
    comment: nameof((a: ProductionStageType) => a.comment) as string,
};

export function formatProductionStage(stage: ProductionStageSnapshotType | null) {
    let result = "";

    if (stage) {
        const schema = Schema();

        result += `${getFieldLabel(fields.name, schema, null)}: ${stage.name}\n`;
        result += `${getFieldLabel(fields.comment, schema, null)}: ${stage.comment}\n`;
    }

    return result;
}
